import { useAppStore } from '@/lib/store';
import { calculateTodayKpis } from '@/lib/calculations';
import { formatTrx, formatNumber, formatUsd } from '@/lib/utils';
import {
  Zap,
  Wifi,
  TrendingUp,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
} from 'lucide-react';

export default function Kpis() {
  const { rows, trxPrice } = useAppStore();

  if (rows.length === 0) {
    return (
      <div className="card p-6 bg-white/5 backdrop-blur-sm border border-slate-700/50">
        <h2 className="text-lg font-semibold mb-4 text-white">
          Today's Resource Requirements
        </h2>
        <div className="text-slate-400 text-center py-8">
          Configure your transfer settings to see personalized staking
          recommendations.
        </div>
      </div>
    );
  }

  const kpis = calculateTodayKpis(rows);

  const kpiCards = [
    {
      title: 'TRX to Stake for Energy',
      value: formatTrx(kpis.trxNeeded),
      subtitle: `Covers ${formatNumber(kpis.energyDay)} Energy/day`,
      explanation:
        'Stake this TRX to get daily renewable energy quota. Your USDT transfers will be free from energy costs.',
      icon: Zap,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/20',
      trend: 'up',
      trendValue: 'Energy',
    },
    {
      title: 'TRX to Stake for Bandwidth',
      value: formatTrx(kpis.trxForBandwidth),
      subtitle: `Covers ${formatNumber(kpis.bandwidthDay)} bytes/day`,
      explanation:
        kpis.bandwidthDay > 600
          ? `Stake this TRX to get daily renewable bandwidth quota beyond the free 600 bytes/day. Covers ${formatNumber(kpis.bandwidthDay - 600)} additional bytes needed.`
          : `No additional TRX needed! Your ${formatNumber(kpis.bandwidthDay)} bytes/day is within the free 600 bytes daily allowance.`,
      icon: Wifi,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/20',
      trend: 'up',
      trendValue: kpis.bandwidthDay > 600 ? 'Beyond Free' : 'Free Coverage',
    },
    {
      title: 'Total TRX to Stake (Recommended)',
      value: formatTrx(kpis.totalTrxNeeded),
      subtitle: formatUsd(kpis.totalTrxNeeded * trxPrice),
      explanation:
        'Stake this amount to cover both energy and bandwidth needs. Your daily transactions will be completely free.',
      icon: TrendingUp,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/20',
      trend: 'up',
      trendValue: 'Total',
    },
    {
      title: 'Daily Cost Without Staking',
      value: formatTrx(kpis.dailyCostWithoutStaking),
      subtitle: formatUsd(kpis.dailyCostWithoutStaking * trxPrice),
      explanation:
        "What you'd pay daily if you don't stake TRX. This is what you avoid by staking the recommended amount.",
      icon: DollarSign,
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/20',
      trend: 'down',
      trendValue: 'Avoided',
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Resource Overview
          </h2>
          <p className="text-slate-400 text-lg">
            Your daily TRON resource requirements and staking recommendations
          </p>
        </div>
        <div className="hidden sm:flex items-center space-x-3 text-sm text-slate-500">
          <span>Live data from TRON network</span>
          <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.map((card, index) => (
          <div
            key={index}
            className={`p-6 rounded-xl border ${card.borderColor} ${card.bgColor} backdrop-blur-sm min-h-[280px] flex flex-col`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 rounded-xl ${card.bgColor}`}>
                <card.icon className={`h-6 w-6 ${card.color}`} />
              </div>
              <div className="flex items-center space-x-2 text-sm">
                {card.trend === 'up' ? (
                  <ArrowUpRight className="h-4 w-4 text-green-400" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 text-red-400" />
                )}
                <span
                  className={
                    card.trend === 'up' ? 'text-green-400' : 'text-red-400'
                  }
                >
                  {card.trendValue}
                </span>
              </div>
            </div>

            <h3 className="font-semibold text-base text-slate-300 mb-3 leading-tight">
              {card.title}
            </h3>
            <p className="text-2xl font-bold text-white mb-3 leading-tight">
              {card.value}
            </p>
            <p className="text-sm text-slate-400 mb-4 leading-tight">
              {card.subtitle}
            </p>
            <p className="text-xs text-slate-500 leading-relaxed flex-grow">
              {card.explanation}
            </p>
          </div>
        ))}
      </div>

      {/* Free Transaction Status */}
      <div className="mt-8 p-6 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-xl border border-green-500/20">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-green-500/20 rounded-xl">
              <CheckCircle className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <h4 className="text-xl font-bold text-white">
                Free Transaction Status
              </h4>
              <p className="text-green-300">
                When you stake the recommended amount
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-green-400">100% Free</div>
            <div className="text-sm text-green-300">Daily transactions</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="p-3 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center space-x-2 mb-2">
              <Zap className="h-4 w-4 text-blue-400" />
              <span className="font-medium text-blue-300">Energy Costs</span>
            </div>
            <div className="text-white">✅ Covered by staking</div>
            <div className="text-slate-400 text-xs">Daily renewable quota</div>
          </div>

          <div className="p-3 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center space-x-2 mb-2">
              <Wifi className="h-4 w-4 text-green-400" />
              <span className="font-medium text-green-300">
                Bandwidth Costs
              </span>
            </div>
            <div className="text-white">
              {kpis.bandwidthDay > 600
                ? '✅ Covered by staking'
                : '✅ Within free limit'}
            </div>
            <div className="text-slate-400 text-xs">
              {kpis.bandwidthDay > 600
                ? `${formatNumber(kpis.bandwidthDay - 600)} bytes beyond 600 free/day`
                : `${formatNumber(kpis.bandwidthDay)} bytes within 600 free/day`}
            </div>
          </div>

          <div className="p-3 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign className="h-4 w-4 text-purple-400" />
              <span className="font-medium text-purple-300">Daily Savings</span>
            </div>
            <div className="text-white">
              💰 {formatUsd(kpis.dailyCostWithoutStaking * trxPrice)}
            </div>
            <div className="text-slate-400 text-xs">What you avoid daily</div>
          </div>
        </div>

        <div className="mt-4 p-4 bg-green-500/20 rounded-lg border border-green-500/30">
          <p className="text-sm text-green-200 leading-relaxed">
            <strong>🎯 Key Benefit:</strong> By staking{' '}
            {formatTrx(kpis.totalTrxNeeded)} TRX, you get a daily renewable
            quota that covers both energy and bandwidth needs. This means your
            USDT transfers become completely free, and you avoid burning{' '}
            {formatTrx(kpis.dailyCostWithoutStaking)} TRX daily.
          </p>
        </div>
      </div>

      {/* Additional Context */}
      <div className="mt-8 p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
        <h4 className="font-semibold mb-4 text-white text-lg">
          How This Works
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-base text-slate-300">
          <div>
            <h5 className="font-medium text-blue-400 mb-3 text-lg">
              Energy (Smart Contracts)
            </h5>
            <p className="text-slate-400 leading-relaxed">
              Each USDT transfer consumes ~65,000 Energy units. Energy
              regenerates daily from your staked TRX.
            </p>
            <div className="mt-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <p className="text-sm text-blue-200 leading-relaxed">
                <strong>Stake {formatTrx(kpis.trxNeeded)} TRX</strong> to get
                daily renewable energy quota. This covers{' '}
                {formatNumber(kpis.energyDay)} Energy/day and makes energy costs
                completely free.
              </p>
            </div>
          </div>
          <div>
            <h5 className="font-medium text-green-400 mb-3 text-lg">
              Bandwidth (Transaction Size)
            </h5>
            <p className="text-slate-400 leading-relaxed">
              Covers the byte size of transactions. Each transfer uses ~350
              bytes of bandwidth.
            </p>
            <div className="mt-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <p className="text-sm text-green-200 leading-relaxed">
                <strong>Free Allowance:</strong> TRON provides 600 free
                bandwidth bytes per day. Only usage beyond this limit requires
                staking or incurs charges.
              </p>
            </div>
            {kpis.trxForBandwidth > 0 ? (
              <div className="mt-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                <p className="text-sm text-green-200 leading-relaxed">
                  <strong>Stake {formatTrx(kpis.trxForBandwidth)} TRX</strong>{' '}
                  to cover bandwidth beyond the free limit. This covers{' '}
                  {formatNumber(kpis.bandwidthDay - 600)} additional bytes/day
                  and makes bandwidth costs completely free.
                </p>
              </div>
            ) : (
              <div className="mt-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <p className="text-sm text-blue-200 leading-relaxed">
                  <strong>No Staking Needed:</strong> Your{' '}
                  {formatNumber(kpis.bandwidthDay)} bytes/day is within the free
                  600 bytes daily allowance. Bandwidth costs are already free!
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="mt-6 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
          <p className="text-sm text-blue-200 leading-relaxed">
            <strong>🎯 Pro Tip:</strong> Stake{' '}
            <strong>{formatTrx(kpis.totalTrxNeeded)} TRX total</strong> to cover
            both energy and bandwidth needs. This gives you a daily renewable
            quota that makes your USDT transfers completely free, saving you{' '}
            {formatUsd(kpis.dailyCostWithoutStaking * trxPrice)} daily.
          </p>
        </div>
      </div>
    </div>
  );
}
